import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { translations, LanguageCode } from '../i18n/translations';

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: (key: keyof typeof translations['en']) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    const saved = localStorage.getItem('beyouramp-lang');
    if (saved && (saved === 'en' || saved === 'it')) {
      return saved as LanguageCode;
    }
    return 'en';
  });

  const setLanguage = useCallback((lang: LanguageCode) => {
    if (lang === 'en' || lang === 'it') {
        setLanguageState(lang);
        localStorage.setItem('beyouramp-lang', lang);
    }
  }, []);

  const t = useCallback((key: keyof typeof translations['en']): string => {
    return translations[language][key] || translations['en'][key] || key;
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};